import './pie-chart.scss';
import {Box, Typography, useTheme} from '@mui/material';
import {useState, useEffect} from 'react';
import {VictoryPie, VictoryTooltip} from 'victory';
import {ChartCard} from '../chart-card/chart-card';
import {translations} from '../../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {ErrorBox} from '../../error-box/error-box';

export interface PieChartDatum {
  id: string;
  x: string;
  y: number;
  color: string;
}

export type PieChartData = Array<PieChartDatum>;

// Get total
const getTotal = (data: Array<PieChartDatum>) => {
  let total: number = 0;
  for (let i = 0; i < data.length; i++) {
    total += data[i].y;
  }
  return total;
};

/**
 * Pie chart used in the statistics dashboards:
 * - x: string
 * - y: number (with unit or not)
 */
export const PieChart = (props: {
  title: string;
  data: PieChartData;
  xLabel: string;
  yLabel: string;
  loading: boolean;
  handleRefresh: () => void;
  yUnitLabel?: string;
  subtitle?: string;
  error?: string;
}) => {
  // Hold state
  const [enabledSlice, setEnabledSlice] = useState<string | undefined>();
  const [activatedLegend, setActivatedLegend] = useState<string | undefined>();
  const [activatedSlice, setActivatedSlice] = useState<string | undefined>();
  const [data, setData] = useState<PieChartData>(props.data);
  const [dataTotal, setDataTotal] = useState<number>(getTotal(props.data));

  // Get theme
  const theme = useTheme();

  // Translations
  const {t} = useTranslation();

  // Update the data
  useEffect(() => {
    // Reset values
    setEnabledSlice(undefined);
    setActivatedLegend(undefined);
    setActivatedSlice(undefined);

    // Set data
    setData(props.data);
    setDataTotal(getTotal(props.data));
  }, [props.data]);

  // Mouse over a legend
  const handleMouseOverLegend = (id: string) => {
    // Update the activated slice when the slice is not disabled, i.e., no slice
    // is enabled or the one that is enabled is the slice we are activating.
    if (activatedSlice !== id && (!enabledSlice || (enabledSlice && enabledSlice === id))) {
      setActivatedSlice(id);
    }
  };

  // Mouse out of a legend
  const handleMouseOutLegend = (id: string) => {
    if (activatedSlice && activatedSlice === id) {
      setActivatedSlice(undefined);
    }
  };

  // Click on legend
  const handleClickLegend = (id: string) => {
    if (enabledSlice && enabledSlice === id) {
      // Update
      setEnabledSlice(undefined);

      // Update data and total
      setData(props.data);
      setDataTotal(getTotal(props.data));

      // Trigger mouse out
      handleMouseOutLegend(id);
    } else {
      // Update
      setEnabledSlice(id);

      // Update data and total
      const newData = [...props.data].filter((obj) => obj.id === id);
      setData(newData);
      setDataTotal(getTotal(newData));

      // Trigger mouse out
      handleMouseOutLegend(id);
    }
  };

  // Mouse over a slice
  const handleMouseOverSlice = (id: string) => {
    if (activatedLegend !== id) {
      setActivatedLegend(id);
    }
  };

  // Mouse out of a slice
  const handleMouseOutSlice = (id: string) => {
    if (activatedLegend && activatedLegend === id) {
      setActivatedLegend(undefined);
    }
  };

  return (
    <ChartCard
      title={props.title}
      subtitle={props.subtitle}
      loading={props.loading}
      handleRefresh={props.handleRefresh}
    >
      <Box className="pie-chart-container">
        {props.data.length > 0 && !props.error && (
          <>
            <VictoryPie
              data={data}
              labels={({datum}) =>
                `${props.xLabel}: ${(datum as PieChartDatum).x}\n \n${props.yLabel}: ${(
                  datum as PieChartDatum
                ).y?.toFixed(props.yUnitLabel ? 2 : 0)}${
                  props.yUnitLabel ? ` ${props.yUnitLabel}` : ''
                } (${(((datum as PieChartDatum).y / dataTotal) * 100)?.toFixed(2)}%)`
              }
              labelComponent={
                <VictoryTooltip
                  constrainToVisibleArea
                  pointerLength={0}
                  flyoutStyle={{
                    stroke: theme.palette.divider,
                    fill: theme.palette.background.default,
                  }}
                />
              }
              height={180}
              innerRadius={180 / 4}
              padAngle={1}
              padding={1}
              style={{
                labels: {
                  fontSize: 11,
                  fontFamily: 'Roboto,sans-serif',
                  fill: theme.palette.text.primary,
                } as any,
                data: {
                  fill: ({datum}: any) => (datum as PieChartDatum).color,
                  fillOpacity: ({datum}: any) =>
                    activatedSlice
                      ? activatedSlice === (datum as PieChartDatum).id
                        ? 0.8
                        : 0.2
                      : 0.6,
                  stroke: ({datum}: any) => (datum as PieChartDatum).color,
                  strokeOpacity: ({datum}: any) =>
                    activatedSlice ? (activatedSlice === (datum as PieChartDatum).id ? 1 : 0.2) : 1,
                  strokeWidth: 1,
                },
              }}
              events={[
                {
                  target: 'data',
                  eventHandlers: {
                    onMouseOver: (_, {datum}) => {
                      return [
                        {
                          target: 'data',
                          mutation: () => ({
                            style: {
                              fill: ({datum}: any) => (datum as PieChartDatum).color,
                              fillOpacity: 0.8,
                              stroke: ({datum}: any) => (datum as PieChartDatum).color,
                              strokeOpacity: 1,
                              strokeWidth: 1,
                            },
                          }),
                          callback: handleMouseOverSlice((datum as PieChartDatum).id),
                        },
                        {
                          target: 'labels',
                          mutation: () => ({active: true}),
                        },
                      ];
                    },
                    onMouseOut: (_, {datum}) => {
                      return [
                        {
                          target: 'data',
                          mutation: () => {},
                          callback: handleMouseOutSlice((datum as PieChartDatum).id),
                        },
                        {
                          target: 'labels',
                          mutation: () => ({active: false}),
                        },
                      ];
                    },
                  },
                },
              ]}
            />
            <Box className="pie-chart-legend">
              {props.data.map((obj) => {
                return (
                  <Box
                    key={`pie-chart-legend-${obj.id}`}
                    className={`pie-chart-legend-row ${
                      enabledSlice && obj.id !== enabledSlice ? 'pie-chart-legend-row-disabled' : ''
                    }`}
                    onMouseOver={() => handleMouseOverLegend(obj.id)}
                    onMouseOut={() => handleMouseOutLegend(obj.id)}
                    onClick={() => handleClickLegend(obj.id)}
                  >
                    <Box
                      className="pie-chart-legend-dot"
                      sx={{
                        backgroundColor: obj.color,
                      }}
                    />
                    <Typography
                      className="pie-chart-legend-text"
                      variant="subtitle2"
                      sx={{
                        fontWeight:
                          activatedLegend && activatedLegend === obj.id ? 'bold' : undefined,
                      }}
                    >
                      {obj.x}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
        {props.data.length === 0 && !props.error && (
          <Typography variant="subtitle2" component="span" color="text.secondary">
            {t(translations.charts.no_data)}
          </Typography>
        )}
        {props.error && <ErrorBox error={props.error} />}
      </Box>
    </ChartCard>
  );
};
